;(function () {
  'use strict'

  angular.module('contrast.pendo').factory('pendoService', PendoService)

  function PendoService(
    $rootScope,
    $window,
    $log,
    ProfileService,
    ProfileFactory,
    $q
  ) {
    'ngInject'

    var service = {
      initialize: initialize,
      initializeIfNeeded: initializeIfNeeded,
      initializeAnonymous: initializeAnonymous
    }

    return service

    /**
     * Sends an identification request to Pendo if it hasn't been sent. Useful when you reload a page
     * Valid fields as defined {@link https://help.pendo.io/resources/support-library/installation/metadata.html}
     */
    function initializeIfNeeded() {
      /* eslint-disable no-undef */
      if (_isEnabled()) {
        if ($window.pendo.accountId == null) {
          initialize()
        }
      }
      /* eslint-enable */
    }

    /**
     * We require org diagnostics_collection_enabled to be true to initialize pendo.
     * If diagnostics_collection_enabled, initialize pendo. Disable pendo if the request comes back false
     */
    function initialize(user, org) {
      if ($rootScope.isEmbeddedInNS) {
        return
      }

      if (!_isEnabled()) {
        return
      }

      if (org) {
        $q.all([_getDiagnosticsCollectionEnabled(org.organization_uuid)]).then(
          function (results) {
            ProfileService.setDiagnosticsCollectionEnabled(results[0])
            if (ProfileService.isDiagnosticsCollectionEnabled() && user) {
              _initializePendoAgent(user, org)
            } else if (!ProfileService.isDiagnosticsCollectionEnabled()) {
              _disableAgent()
            }
          }
        )
      }
    }

    /**
     * Register an anonymous visitor & account.
     * This method will be used when the user session is invalidated.
     */
    function initializeAnonymous() {
      /* eslint-disable no-undef */
      if (_isEnabled()) {
        try {
          var host = _getHost()
          $window.pendo.initialize({
            visitor: {
              id: 'anonymous / ' + host,
              company_domain: 'anonymous'
            },
            account: {
              id: 'anonymous @ ' + host,
              name: 'anonymous',
              host: host
            }
          })
        } catch (e) {
          $log.error('Unable to communicate with Pendo')
          $log.error(e)
        }
      }
      /* eslint-enable */
    }

    /**
     * Sends an identification request to Pendo.
     * Valid fields as defined {@link https://help.pendo.io/resources/support-library/installation/metadata.html}
     */
    function _initializePendoAgent(user, org) {
      /* eslint-disable no-undef */
      if (_isEnabled()) {
        _enableAgent()
        try {
          var host = _getHost()
          if (user && org) {
            var request = _createRequest(user, org, host)
            if (_hasBeenInitialized()) {
              _updateVisitor(request)
            } else {
              _initializeVisitor(request)
            }
          }
        } catch (e) {
          $log.error('Unable to communicate with Pendo')
          $log.error(e)
        }
      }
      /* eslint-enable */
    }

    function _createRequest(user, org, host) {
      var promises = []
      promises.push(
        _hash(user.email),
        _getCompanyDomain(user.email),
        _createAccount(org, host)
      )

      if (!org.freemium) {
        promises.push(_getFreeTrialData(org))
      }

      return $q.all(promises).then(function (results) {
        const visitorId = results[0]
        const companyDomain = results[1]
        const account = results[2]
        const visitor = {
          id: visitorId,
          company_domain: companyDomain,
          ip_address: user.ip_address
        }
        if (!org.freemium) {
          const freeTrialData = results[3]
          account.state = freeTrialData.free_trial_status || ''
          visitor.record_id = freeTrialData.is_creator
            ? freeTrialData.record_id
            : ''
        }
        return {
          visitor,
          account
        }
      })
    }

    /**
     * @param {promise} request
     * Update initialized pendo agent with new visitor credentials.
     */
    function _updateVisitor(request) {
      $q.all([request]).then(function (results) {
        $window.pendo.updateOptions(results[0])
      })
    }

    /**
     * @param {promise} request
     * Inititialize agent with new visitor credentials
     */
    function _initializeVisitor(request) {
      $q.all([request]).then(function (results) {
        $window.pendo.initialize(results[0])
      })
    }

    /**
     * Disables the agent
     */
    function _disableAgent() {
      $window.pendo.stopSendingEvents()
    }

    /**
     * Enables the agent
     */
    function _enableAgent() {
      $window.pendo.startSendingEvents()
    }

    /**
     * Gets host name and classifies the environment by its domain extention
     * Example 1: app.contrastsecurity.com -> app
     * Example 2: app.contrastsecurity.jp -> app-jp
     */
    function _getHost() {
      var host = $window.location.hostname || 'unknown'

      if (!host.includes('.com')) {
        if (host === '127.0.0.1') {
          return 'localhost'
        } else if (host.includes('.')) {
          var hostArray = host.split('.')
          if (hostArray.length >= 3) {
            return hostArray[0] + '-' + hostArray[2]
          }
        }
      }
      return host.split('.')[0]
    }

    /**
     * Whether the Pendo snippet was added to the browser
     */
    function _isEnabled() {
      return typeof $window.pendo !== 'undefined'
    }

    /**
     * Whether the Pendo agent was initialized
     */
    function _hasBeenInitialized() {
      return typeof $window.pendo.visitorId !== 'undefined'
    }

    /**
     * Checks the org to see if it has diagnostics_collection_enabled
     */
    function _getDiagnosticsCollectionEnabled(orgUuid) {
      return ProfileFactory.getOrgDiagnosticsSettings(orgUuid).then(function (
        response
      ) {
        return response.data.settings.diagnostics_collection_enabled
      })
    }

    /**
     *  Creates the account object
     */
    function _createAccount(org, host) {
      return $q
        .all([_hash(org.organization_uuid), _hash(org.name)])
        .then(function (results) {
          const accountId = results[0]
          const name = results[1]
          return {
            id: accountId,
            name: name,
            freemium: org.freemium,
            host: host,
            apps_onboarded: org.apps_onboarded,
            org_name: org.name
          }
        })
    }

    /**
     *  Get the free trial organization data
     */
    function _getFreeTrialData(org) {
      return ProfileFactory.getFreeTrialOrgData(org.organization_uuid).then(
        function (response) {
          return response.data
        }
      )
    }

    /**
     * returns the text after '@' for an email address
     * @param {String} email
     */
    function _getCompanyDomain(email) {
      var regex = '[@]([a-zA-Z0-9\\.]*)'
      var results = email.match(regex)

      return results ? _hash(results[1]) : '?'
    }

    /**
     * SHA-1 and Base64 (URL safe) encodes a string
     * This encryption is done on the backend so we can refer to the same variables
     */
    function _hash(string) {
      /* eslint-disable no-undef */
      var data = new TextEncoder().encode(string)

      return crypto.subtle.digest('SHA-1', data).then(function (buffer) {
        var bytes = new Uint8Array(buffer)
        return _urlSafeBase64Encode(bytes)
      })
      /* eslint-enable */
    }

    function _urlSafeBase64Encode(array) {
      /* eslint-disable no-undef */
      return btoa(String.fromCharCode.apply(null, array))
        .replace(/\+/g, '-') // Convert '+' to '-'
        .replace(/\//g, '_') // Convert '/' to '_'
        .replace(/=+$/, '') // Remove ending '='
      /* eslint-enable */
    }
  }
})()
